import { SavedAirResponseDataDTO } from "./response.types";
import { AirStatusesModel } from "entities/shared/airCard.types";
import { ApiShipmentType, CoordinateModelDTO, DateTimeDTO } from "entities/shared/shipmentCard.types";
import { DeepPartial, ValueOf } from "shared/lib/typeCheck.lib";

export type AirWhereQuery = Partial<
  Pick<SavedAirResponseDataDTO, "reference" | "carrier_scac" | "carrier_name" | "status" | "tags">
> & {
  location?: Pick<SavedAirResponseDataDTO["location"], "from">;
  /**
   * Which list the cards belong to. Sea and air share one table in the back
   * office, so every read has to say which of the two it is asking for; the
   * API layer fills it in, callers do not.
   */
  shipment_type?: ValueOf<typeof ApiShipmentType>;
};

export type OrderQueryColumn =
  | keyof Pick<
      SavedAirResponseDataDTO,
      "id" | "reference" | "carrier_scac" | "carrier_name" | "status" | "updated_at" | "created_at"
    >
  | ("location_from" | "location_to" | "date_from" | "date_from_actual" | "date_to" | "date_to_actual");

export interface AirOrderQuery {
  column: OrderQueryColumn;
  asc: boolean;
}

export type GetSingleCardParams = Pick<GetCardsQuery, "where">;

export interface GetCardsQuery {
  limit?: number | null;
  offset?: number | null;
  where?: AirWhereQuery;
  order?: AirOrderQuery[];
}

export type UpdateTagsField = string | string[];
interface UpdateTags {
  tags?: SavedAirResponseDataDTO["tags"] | ({ add?: UpdateTagsField } & { remove?: UpdateTagsField });
}

export interface FollowCardQuery
  extends Partial<
    Omit<SavedAirResponseDataDTO, "created_at" | "type" | "date" | "carrier_scac" | "carrier_name" | "tags">
  > {
  reference: string;
  /**
   * Sea and air share one saved list, and the payload alone does not say which
   * mode the card was tracked in.
   */
  shipment_type: ValueOf<typeof ApiShipmentType>;
  /** Air is only ever tracked by waybill. */
  type: "AWB";
  /** The airline's IATA code - the saved card names the column after ocean. */
  carrier_scac: string | null;
  carrier_name: string | null;
  status: ValueOf<typeof AirStatusesModel>;
  /** Pieces of cargo. */
  equipments_count: number;
  /** Pieces and weight as a JSON array - see `formatAirCargo`. */
  size_type: string | null;
  location: {
    from: string | null;
    to: string | null;
    transport: "AIR";
    pin: CoordinateModelDTO | null;
  };
  tags: string[];
  date: {
    from: DateTimeDTO | null;
    from_actual: boolean;
    to: DateTimeDTO | null;
    to_actual: boolean;
    to_predictive: DateTimeDTO | null;
  };
}

export type PreparedDataToUpdateCardReturnType = FollowCardQuery;

export type UpdateCardData = DeepPartial<Omit<PreparedDataToUpdateCardReturnType, "id" | "profile">> & UpdateTags;
export interface UpdateCardQuery {
  id: number;
  data: UpdateCardData;
}
export interface UpdateCardsQuery {
  where: AirWhereQuery;
  update: UpdateCardData;
}
